import { Dialog } from '@headlessui/react';
import { AlertTriangle, CreditCard, Landmark, Loader2, ShieldCheck, X } from 'lucide-react';
import { euros } from '../../lib/orders/devis';
import type { OffreDePaiement } from '../../lib/paiement/stripe';

/*
  La fenêtre qui s'ouvre AVANT la commande, et qui ne demande qu'une chose :
  comment le parent veut payer.

  ## Ce qu'elle disait, et pourquoi c'était un défaut

  Sa première version s'ouvrait sur un bandeau vert : « C'est fait : votre
  enfant est inscrit — le paiement ne conditionne pas l'inscription. » Signalé
  le 6 septembre 2026, après le premier paiement réel :

    « quand je clique sur valider l'inscription, j'ai directement la
      confirmation que c'est fait, du coup mon premier réflexe est de ne pas
      aller au paiement »

  Le reproche est juste, et il vise ce que j'avais écrit. Rassurer sur
  l'inscription **avant** de demander le paiement retire toute raison de payer :
  on vient d'annoncer au parent qu'il n'a plus rien à faire.

  La fenêtre annonce donc maintenant une **étape**, pas un aboutissement. La
  confirmation, elle, vient après — quand le parent a choisi.

  ## Rien n'est écrit tant que rien n'est choisi

  Sa deuxième version s'ouvrait APRÈS `create_order` : la commande avait déjà
  commité, et la fenêtre annonçait « votre place est réservée le temps de
  choisir ». Signalé le 10 septembre 2026 :

    « n'ayant pas de croix pour sortir de cette boxe j'ai fermé l'onglet car
      je ne voulais plus valider cette inscription. Je reviens cet après-midi
      dessus et je vois que l'inscription a été validée quand même... »

  Elle promettait aussi une libération automatique au bout de 30 minutes. Cette
  promesse reposait sur `checkout.session.expired` — mais sans clic sur
  Bancontact, aucune session Stripe n'existe : rien n'expirait, donc rien
  n'annulait. La fenêtre affirmait quelque chose de faux.

  L'ordre est inversé depuis : **c'est le choix qui écrit** (voir
  `lib/orders/tunnel.ts`). La fenêtre peut donc dire la vérité — tant que le
  parent n'a pas choisi, rien n'est enregistré — et elle porte une croix, qui
  ne fait alors rien d'autre que la fermer.

  ## Deux boutons, deux poids

  Bancontact est plein et coloré ; le virement est un bouton bordé. Demandé
  ainsi : « plus visible sans le mettre en valeur ». Le second reste un vrai
  bouton — pas un lien souligné qu'on cherche — parce que c'est par lui que
  passent les familles sans carte belge, et parce qu'un choix qu'on ne trouve
  pas n'est pas un choix.

  Son libellé porte l'engagement : cliquer, c'est s'engager à payer sous
  15 jours.
*/

export interface ModalePaiementProps {
  isOpen: boolean;
  /** Fermer sans choisir. Rien n'a été écrit : il n'y a rien à défaire. */
  onClose: () => void;
  /** Commander, puis partir chez Stripe. */
  onPayerMaintenant: () => void;
  /** Commander : l'inscription vaut, la facture part, payable sous 15 jours. */
  onPayerPlusTard: () => void;
  offre: OffreDePaiement;
  /**
   * Quel geste est en vol, ou `null`.
   *
   * Un booléen ne suffisait plus. Depuis que les DEUX boutons écrivent la
   * commande, un drapeau commun faisait afficher « Ouverture du paiement… »
   * sous le bouton Bancontact pendant que le parent attendait son virement.
   */
  enCours: 'stripe' | 'virement' | null;
  erreur?: string | null;
  /**
   * Ce que le parent retrouve s'il ferme sans choisir. Les stages restent dans
   * le panier ; une journée pédagogique, qui ne passe pas par le panier, reste
   * cochée sur sa page.
   */
  ceQuiResteEnFermant?: string;
}

export const ModalePaiement = ({
  isOpen, onClose, onPayerMaintenant, onPayerPlusTard, offre, enCours, erreur,
  ceQuiResteEnFermant = 'vos semaines restent dans votre panier',
}: ModalePaiementProps) => {
  const payable = offre.forme === 'payable';
  const occupe = enCours !== null;

  return (
    <Dialog
      open={isOpen}
      onClose={occupe ? () => {} : onClose}
      className="fixed inset-0 z-50 overflow-y-auto"
    >
      <div className="flex min-h-screen items-center justify-center p-4">
        <Dialog.Overlay className="fixed inset-0 bg-black bg-opacity-40" />

        <div className="relative bg-white rounded-xl shadow-xl max-w-md w-full mx-auto overflow-hidden">

          {/*
            --- L'en-tête ---
            Une ÉTAPE, pas un aboutissement. Le vert et le « c'est fait »
            viennent après le choix, jamais avant.

            La croix est une SORTIE, pas une décoration : sans elle, un parent
            qui se ravise n'a d'autre issue que de fermer l'onglet — et c'est
            exactement ce qui s'est passé le 10 septembre.
          */}
          <div className="border-b border-gray-200 px-6 py-5 pr-14">
            <Dialog.Title className="text-xl font-bold text-gray-900">
              Dernière étape : le paiement
            </Dialog.Title>
            <p className="text-sm text-gray-600 mt-1">
              Choisissez comment régler ; l'inscription est enregistrée à ce moment-là.
            </p>
          </div>

          <button
            type="button"
            onClick={onClose}
            disabled={occupe}
            aria-label="Fermer sans valider l'inscription"
            title="Fermer sans valider l'inscription"
            className="absolute top-4 right-4 rounded-md p-1.5 text-gray-400
                       transition hover:bg-gray-100 hover:text-gray-700
                       focus:outline-none focus:ring-2 focus:ring-gray-400
                       disabled:opacity-40 disabled:hover:bg-transparent"
          >
            <X className="h-5 w-5" />
          </button>

          <div className="px-6 py-5">
            {payable ? (
              <>
                <p className="text-sm text-gray-600 mb-1">Montant à régler</p>
                <p className="text-3xl font-bold text-gray-900 mb-5">
                  {euros(offre.montantCentimes)}
                </p>

                {/* --- Bancontact : plein, coloré --- */}
                <button
                  type="button"
                  onClick={onPayerMaintenant}
                  disabled={occupe}
                  className="w-full inline-flex items-center justify-center gap-2 rounded-lg
                             bg-primary-600 px-4 py-4 text-base font-semibold text-white
                             shadow-sm transition hover:bg-primary-700
                             focus:outline-none focus:ring-2 focus:ring-primary-500
                             focus:ring-offset-2 disabled:opacity-60"
                >
                  {enCours === 'stripe' ? (
                    <>
                      <Loader2 className="h-5 w-5 animate-spin" />
                      Ouverture du paiement…
                    </>
                  ) : (
                    <>
                      <CreditCard className="h-5 w-5" />
                      Payer maintenant par Bancontact
                    </>
                  )}
                </button>
                <p className="mt-2 text-center text-xs text-gray-500">
                  Paiement sécurisé. Inscription et facture acquittée immédiates.
                </p>

                {/*
                  --- Le virement : bordé, visible, sans être mis en valeur ---
                  Le libellé EST l'engagement. Cliquer vaut acceptation de payer
                  sous 15 jours ; l'inscription est alors définitive.
                */}
                <div className="mt-5 pt-5 border-t border-gray-200">
                  <button
                    type="button"
                    onClick={onPayerPlusTard}
                    disabled={occupe}
                    className="w-full inline-flex items-center justify-center gap-2 rounded-lg
                               border border-gray-300 bg-white px-4 py-3 text-sm font-medium
                               text-gray-700 transition hover:bg-gray-50
                               focus:outline-none focus:ring-2 focus:ring-gray-400
                               focus:ring-offset-2 disabled:opacity-50"
                  >
                    {enCours === 'virement' ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin" />
                        Enregistrement de l'inscription…
                      </>
                    ) : (
                      <>
                        <Landmark className="h-4 w-4" />
                        Payer par virement sous 15 jours
                      </>
                    )}
                  </button>
                  <p className="mt-2 text-center text-xs text-gray-500">
                    En choisissant le virement, vous confirmez l'inscription et vous
                    vous engagez à régler la facture dans les 15 jours. Elle vous
                    parvient par email, avec un QR code.
                  </p>
                </div>

                {/*
                  --- Ce que fermer veut dire ---
                  Rien. Et c'est précisément ce qu'il faut dire : le parent qui
                  hésite doit savoir qu'il peut sortir sans conséquence, au
                  lieu de fermer l'onglet en croyant s'être engagé.
                */}
                <div className="mt-5 flex items-start gap-2 rounded-md bg-gray-50 p-3">
                  <ShieldCheck className="h-4 w-4 text-gray-400 flex-shrink-0 mt-0.5" />
                  <p className="text-xs text-gray-500">
                    Tant que vous n'avez pas choisi, rien n'est enregistré. Vous pouvez
                    fermer cette fenêtre : {ceQuiResteEnFermant}.
                  </p>
                </div>
              </>
            ) : (
              <>
                {/*
                  Rien à payer, ou trop peu pour Stripe. Dans les deux cas le
                  bouton n'a pas lieu d'être : dans le premier il mentirait,
                  dans le second il échouerait après le clic.
                */}
                <p className="text-gray-700">
                  {offre.forme === 'rien_a_payer' ? (
                    <>
                      <span className="font-semibold text-green-700">Rien à payer.</span>{' '}
                      Le montant est entièrement couvert — par un bon cadeau, par
                      votre provision, ou les deux.
                    </>
                  ) : (
                    <>
                      Il reste{' '}
                      <span className="font-semibold">{euros(offre.montantCentimes)}</span>,
                      un montant trop faible pour le paiement en ligne. Il sera repris
                      sur votre facture, payable par virement.
                    </>
                  )}
                </p>

                <button
                  type="button"
                  onClick={onPayerPlusTard}
                  disabled={occupe}
                  className="mt-6 w-full btn-primary py-3 disabled:opacity-60"
                >
                  {enCours === 'virement' ? 'Enregistrement…' : "Confirmer l'inscription"}
                </button>
              </>
            )}

            {/*
              --- Le refus, quel qu'il soit ---
              Hors du choix payable/non payable, parce que `create_order`
              s'appelle maintenant DEPUIS cette fenêtre : son refus (« la
              semaine du … n'a plus assez de places ») peut arriver dans les
              deux cas. Tant qu'il n'écrivait rien, seule l'ouverture de
              Stripe pouvait échouer ici.
            */}
            {erreur && (
              <div className="mt-4 rounded-md border border-amber-200 bg-amber-50 p-3">
                <p className="flex items-start gap-2 text-sm text-amber-900">
                  <AlertTriangle className="h-4 w-4 flex-shrink-0 mt-0.5" />
                  <span>{erreur}</span>
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </Dialog>
  );
};
